<script setup lang="ts">
import { Components } from '@/api/generated/api';
import { ProductFeature } from '@/api/generated/api.enum';
import ChevronDownIcon from '@/assets/icons/ChevronDownIcon.vue';
import { useBillingState } from '@/composables/billing/useBillingState';
import { useCheckoutOfferSource } from '@/composables/billing/useCheckoutOfferSource';
import { STUDENT_AVATAR_ICON_OPTIONS, STUDENT_AVATAR_OPTIONS, TEACHER_AVATAR_OPTIONS } from '@/constants';
import BillingProBadge from '@/core/billing/BillingProBadge.vue';
import BillingCheckoutDialog from '@/core/dialogs/billing-checkout/BillingCheckoutDialog.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import { useDialog } from '@/core/dialogsystem/useDialog';
import { IOnboardingStudent } from '@/interfaces';
import { EPosition } from 'common/enum/misc.enum';
import { MAX_UPLOAD_SIZE_AVATAR_PICTURE } from 'common/constants/file.upload.limits';
import { ALLOWED_FILE_TYPES_AVATAR_PICTURE } from 'common/constants/file.upload.types';
import { isFileSizeExceedingLimit, isFileTypeAllowed } from 'common/utils/file.utils';
import { computed, ref, toRefs, useTemplateRef, watchEffect } from 'vue';
import AvatarButton from './AvatarButton.vue';
import EmptyAvatar from './EmptyAvatar.vue';

export type TAvatarSelection = {
  avatar: string;
  avatarPictureFile?: File | null;
  avatarPictureId?: number | null;
  firstName: string;
  lastName: string;
};

const props = withDefaults(
  defineProps<{
    modelValue: Components.Schemas.StudentDto | IOnboardingStudent | TAvatarSelection | null;
    disabled?: boolean;
    mode?: 'student' | 'teacher';
    allowPictureUpload?: boolean;
  }>(),
  {
    allowPictureUpload: false,
    disabled: false,
    mode: 'student',
  },
);

const menuDialogRef = useTemplateRef('menuDialogRef');
const anchorButtonRef = useTemplateRef('anchorButtonRef');
const fileInputRef = useTemplateRef('fileInputRef');

const { hasFeature } = useBillingState();
const { getFeatureGateOfferSource } = useCheckoutOfferSource();

const { modelValue } = toRefs(props);
const selectedAvatar = ref<string>(props.modelValue?.avatar || '');
const allowedFileTypes = ALLOWED_FILE_TYPES_AVATAR_PICTURE;
const emit = defineEmits<{
  'avatar-picture-cleared': [];
  'file-selected': [file: File];
  'file-size-exceeded': [];
  'file-type-invalid': [];
  'update:modelValue': [avatar: string];
}>();

const handleAvatarSelected = (avatar: string): void => {
  selectedAvatar.value = avatar;
  emit('update:modelValue', avatar);
  emit('avatar-picture-cleared');
  menuDialogRef.value?.hide();
};

const isNameFilled = computed(() => modelValue.value?.firstName || modelValue.value?.lastName);
const canOpenAvatarMenu = computed<boolean>(() => Boolean(isNameFilled.value || canUploadPicture.value));
const isInitialsSelected = computed<boolean>(() => selectedAvatar.value === '');

const handleAnchorButtonClick = (): void => {
  if (menuDialogRef.value?.isOpen) {
    menuDialogRef.value.hide();
    return;
  }

  menuDialogRef.value?.show();
};

const avatarOptions = computed(() => (props.mode === 'teacher' ? TEACHER_AVATAR_OPTIONS : STUDENT_AVATAR_OPTIONS));
const canUploadPicture = computed<boolean>(() => props.allowPictureUpload);

// Uploading a real photo for a student is a paid feature; picking a built-in avatar icon stays free.
// Normal-user avatars (mode === 'teacher') are never gated.
const isUploadLocked = computed<boolean>(
  () => props.mode === 'student' && canUploadPicture.value && !hasFeature(ProductFeature.tenantCustomization),
);

const checkoutDialog = useDialog({ component: BillingCheckoutDialog, props: {} });

const handleUploadClick = (): void => {
  if (isUploadLocked.value) {
    menuDialogRef.value?.hide();
    checkoutDialog.show({ offerSource: getFeatureGateOfferSource(ProductFeature.tenantCustomization) });
    return;
  }
  openFileExplorer();
};

const handlePictureSelected = (files: FileList | null): void => {
  if (!files?.length) return;

  const file = files[0];
  if (!isFileTypeAllowed(file.type, allowedFileTypes)) {
    emit('file-type-invalid');
    return;
  }
  if (isFileSizeExceedingLimit(file.size, MAX_UPLOAD_SIZE_AVATAR_PICTURE)) {
    emit('file-size-exceeded');
    return;
  }

  selectedAvatar.value = '';
  emit('file-selected', file);
  menuDialogRef.value?.hide();
};

const handleFileChange = (event: Event): void => {
  const input = event.target as HTMLInputElement;
  handlePictureSelected(input.files);
  input.value = '';
};

const openFileExplorer = (): void => {
  fileInputRef.value?.click();
};

watchEffect(() => {
  if (props.modelValue) {
    selectedAvatar.value = props.modelValue.avatar || '';
  }
});
</script>

<template>
  <div class="max-w-[75px]">
    <input
      v-if="canUploadPicture"
      ref="fileInputRef"
      type="file"
      :accept="allowedFileTypes.join(',')"
      class="hidden"
      @change="handleFileChange"
    />
    <EmptyAvatar v-if="!canOpenAvatarMenu" />
    <div
      v-else
      ref="anchorButtonRef"
      class="flex items-center cursor-pointer"
      :class="[disabled && 'pointer-events-none cursor-default']"
      @click="handleAnchorButtonClick"
    >
      <div class="rounded-full size-cell flex items-center justify-center">
        <AvatarButton
          :avatar-name="selectedAvatar"
          :avatar-picture-id="modelValue?.avatarPictureId"
          type="button"
          :student="modelValue"
          class="!size-[44px]"
        />
      </div>
      <div class="ml-1 size-icon-xs text-secondary">
        <ChevronDownIcon v-if="!disabled" class="size-icon-xs" />
      </div>
    </div>

    <PositionablePopover
      ref="menuDialogRef"
      scroll-mode="content"
      :anchor-element="anchorButtonRef"
      :position="EPosition.BOTTOM_RIGHT"
    >
      <div class="flex min-h-0 flex-col overflow-hidden">
        <div :class="['min-h-0 overflow-y-auto overscroll-contain', 'w-[280px] max-w-full max-h-[600px]']">
          <div class="p-md">
            <div class="grid grid-cols-5 gap-md">
              <AvatarButton
                :student="modelValue"
                type="button"
                :is-selected="isInitialsSelected"
                :hoverable="true"
                @click="handleAvatarSelected('')"
              />
              <AvatarButton
                v-for="avatar in STUDENT_AVATAR_ICON_OPTIONS"
                :key="avatar"
                :avatar-name="avatar"
                :is-selected="selectedAvatar === avatar"
                :hoverable="true"
                type="button"
                @click="handleAvatarSelected(avatar)"
              />
            </div>
            <span class="my-sm border-divider border-b-1 w-full block"></span>
            <div class="grid grid-cols-5 gap-md">
              <AvatarButton
                v-for="avatar in avatarOptions"
                :key="avatar"
                :avatar-name="avatar"
                :is-selected="selectedAvatar === avatar"
                :hoverable="true"
                type="button"
                @click="handleAvatarSelected(avatar)"
              />
            </div>
            <span v-if="canUploadPicture" class="my-sm border-divider border-b-1 w-full block"></span>
            <button
              v-if="canUploadPicture"
              type="button"
              class="w-full flex items-center gap-sm text-left p-sm outline-inner rounded-sm hover:bg-surface-secondary active:bg-surface-secondary"
              :class="isUploadLocked ? 'text-secondary' : 'text-primary'"
              @click="handleUploadClick"
            >
              {{ $t('avatar.uploadPicture') }}
              <BillingProBadge v-if="isUploadLocked" theme="gray" class="ml-auto" />
            </button>
          </div>
        </div>
      </div>
    </PositionablePopover>
  </div>
</template>
